// Chakra imports
import {
  Box,
  Button,
  Flex,
  Icon,
  Image,
  Link,
  Text,
  Tooltip,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Badge,
  Divider,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import React, { useMemo } from "react";
import { CheckIcon, CloseIcon } from "@chakra-ui/icons";
import Card from "components/card/Card.js";

// Helper: safe JSON parsing
const safeParse = (maybeJSON) => {
  if (!maybeJSON) return null;
  try {
    return typeof maybeJSON === "string" ? JSON.parse(maybeJSON) : maybeJSON;
  } catch (e) {
    return null;
  }
};

const STAGES = [
  "Order Placed",
  "Shipped",
  "In Transit",
  "Out for Delivery",
  "Delivered",
  "Cancelled"
];

export default function NFT(props) {
  const {
    id,
    image,
    name,
    infringingName,
    urls,
    documents,
    otherDetails,
    clientStatus, // null/undefined => pending, 0 => denied, 1 => approved
    orderStage, 
    onApprove,
    onReject,
  } = props;

  const textColor = useColorModeValue("navy.700", "white");
  const subTextColor = useColorModeValue("gray.600", "gray.300");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.300");

  const { isOpen, onOpen, onClose } = useDisclosure();

  const parsedUrls = useMemo(() => safeParse(urls), [urls]);
  const parsedDocs = useMemo(() => safeParse(documents), [documents]);
  const parsedOther = useMemo(() => safeParse(otherDetails), [otherDetails]);

  const isApproved = clientStatus === "1";
  const isDenied = clientStatus === "0";
  const isPending = !isApproved && !isDenied;

  // choose a primary link if available
  const primaryHref = useMemo(() => {
    if (!parsedUrls || typeof parsedUrls !== "object") return undefined;
    const first = Object.values(parsedUrls).find((v) => typeof v === "string");
    return first;
  }, [parsedUrls]);

  return (
    <Card p="16px" w="100%">
      <Box  _hover={{ textDecoration: "none" }}>
        <Flex direction="column" justify="center">
          {/* Image */}
          <Box mb="16px" position="relative" display="flex" justifyContent="center" borderRadius="16px" borderWidth="1px" borderColor={borderColor} overflow="hidden" bg={useColorModeValue("gray.50", "whiteAlpha.100")}>
            <Image src={image} alt={name} h="220px" w="100%" objectFit="contain" fallbackSrc={image} />
          </Box>

          {/* Texts */}
          <Flex direction="column" gap={1}>
            <Text color={textColor} fontSize="lg" fontWeight="700">
              <Box as="span" fontWeight="800">{name}</Box>
            </Text>
            <Text color={subTextColor} fontSize="sm">
              Infringing Brand: <Box as="span" fontWeight="600">{infringingName || "-"}</Box>
            </Text>

            {isApproved && (
              
              <OrderProgress stages={STAGES} orderStage={orderStage} />
            )}
          </Flex>
        </Flex>
      </Box>

      {/* Actions */}
      <Flex mt={4} gap={3} wrap="wrap">
        {isPending && (
          <>
            <Button colorScheme="green" size="sm" onClick={() => onApprove?.(id)}>Approve Test Purchase</Button>
            <Button colorScheme="red" size="sm" variant="outline" onClick={() => onReject?.(id)}>Reject</Button>
          </>
        )}
        {isDenied && (
          <Button colorScheme="green" size="sm" onClick={() => onApprove?.(id)}>Approve Test Purchase</Button>
        )}
        <Button size="sm" onClick={onOpen}>More Details</Button>
      </Flex>

      {/* Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" gap={3}>
              <Box>
                <Text fontWeight="700">Client Brand</Text>
                <Text>{name}</Text>
              </Box>
              <Box>
                <Text fontWeight="700">Infringing Brand</Text>
                <Text>{infringingName || "-"}</Text>
              </Box>

              {parsedOther && (
                <Box>
                  <Text fontWeight="700" mb={1}>Other Details</Text>
                  <KeyValueGrid data={parsedOther} />
                </Box>
              )}

              {parsedUrls && (
                <Box>
                  <Text fontWeight="700" mb={1}>URLs</Text>
                  <KeyValueLinks data={parsedUrls} />
                </Box>
              )}

              {parsedDocs && (
                <Box>
                  <Text fontWeight="700" mb={1}>Documents</Text>
                  <KeyValueLinks data={parsedDocs} />
                </Box>
              )}
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
}


function OrderProgress({ stages, orderStage }) {
  const activeColor = useColorModeValue("green.500", "green.300");
  const baseColor = useColorModeValue("gray.300", "whiteAlpha.400");
  const cancelColor = useColorModeValue("red.500", "red.300"); // added
  console.log("orderStage", orderStage);

  return (
    <Box mt={3}>
      <Flex align="center" gap={2}>
        {stages.map((label, idx) => {
          const stageNum = idx + 1;
          const isDone = stageNum <= orderStage && orderStage !== 6;
          const isCancelled = orderStage === 6 && stageNum === 6;

          return (
            <Flex key={label} align="center" flex="1" minW={0}>
              <Tooltip label={label} hasArrow>
                <Flex
                  align="center"
                  justify="center"
                  w="22px"
                  h="22px"
                  borderRadius="full"
                  bg={isCancelled ? cancelColor : isDone ? activeColor : baseColor}
                  color="white"
                  flexShrink={0}
                >
                  {isDone  ? (
                    <CheckIcon boxSize="12px" />
                  ) : isCancelled ? (<CloseIcon boxSize="12px" />): (
                    <Box w="8px" h="8px" borderRadius="full" bg="white" />
                  )}
                </Flex>
              </Tooltip>
              {idx < stages.length - 1 && (
                <Box
                  flex="1"
                  h="2px"
                  bg={stageNum < orderStage && orderStage !== 6 ? activeColor : baseColor}
                  mx={2}
                  borderRadius="full"
                />
              )}
            </Flex>
          );
        })}
      </Flex>
    </Box>
  );
}


function KeyValueGrid({ data }) {
  const entries = Object.entries(data || {});
  if (!entries.length) return null;
  return (
    <Box borderWidth="1px" borderRadius="md" p={3}>
      <Wrap spacing={3}>
        {entries.map(([k, v]) => (
          <WrapItem key={k} minW="45%">
            <Box>
              <Text fontSize="xs" color="gray.500">{k}</Text>
              <Text fontWeight="600">{String(v)}</Text>
            </Box>
          </WrapItem>
        ))}
      </Wrap>
    </Box>
  );
}

function KeyValueLinks({ data }) {
  const entries = Object.entries(data || {});
  if (!entries.length) return null;
  return (
    <Box borderWidth="1px" borderRadius="md" p={3}>
      {entries.map(([k, v], idx) => {
        const isUrl = typeof v === "string" && /^(https?:\/\/)/i.test(v);
        return (
          <Box key={`${k}-${idx}`}>
            <Flex align="center" justify="space-between" gap={3}>
              <Text fontSize="xs" color="gray.500">{k}</Text>
              {isUrl ? (
                <Link href={v} target="_blank" rel="noopener noreferrer" maxW="70%"   fontWeight="600" isExternal>
                  {v}
                </Link>
              ) : (
                   String(v).length > 50 ? String(v).slice(0, 50) + "..." : String(v) 
              )}
            </Flex>
            {idx < entries.length - 1 && <Divider my={2} />}
          </Box>
        );
      })}
    </Box>
  );
}
